/**
 * Hero foni — koinotdagi Yer va O'zbekistonni skanerlayotgan yo'ldoshlar
 * (`hero3d/scene.ts`).
 *
 * three.js va sahna kodi dinamik `import()` bilan, sahifa chizilib bo'lgach
 * (brauzer bo'sh vaqtida) yuklanadi — dastlabki yuklama va LCP'ga ta'sir
 * qilmaydi. Yuklanguncha koinot rangidagi fon turadi, birinchi kadr tayyor
 * bo'lgach kanvas sekin paydo bo'ladi.
 *
 * Zaxira (`fallback` — CMS'dagi hero rasm(lar)i) quyidagi hollarda
 * ko'rsatiladi: WebGL2 yo'q, sahna yuklanmadi yoki GPU konteksti yo'qoldi,
 * yuqori kontrast mavzusi (ЧТЗ 4.2.12 — fon tasvirlari o'chiriladi).
 * `prefers-reduced-motion` da animatsiya o'rniga bitta statik kadr chiziladi.
 */
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

import { prefersReducedMotion, watchViewport } from "@/shared/lib/motion";
import { usePrefs } from "@/shared/lib/prefs";

import type { GlobeHandle, GlobeStat } from "./hero3d/scene";

type Status = "loading" | "ready" | "failed";

function supportsWebGL2(): boolean {
  try {
    return Boolean(document.createElement("canvas").getContext("webgl2"));
  } catch {
    return false;
  }
}

export function HeroGlobe({ fallback, stats = [] }: { fallback: ReactNode; stats?: GlobeStat[] }) {
  const { theme } = usePrefs();
  const hostRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const labelsRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<Status>(() => (supportsWebGL2() ? "loading" : "failed"));
  const handleRef = useRef<GlobeHandle | null>(null);
  const statsRef = useRef(stats);
  statsRef.current = stats;
  const enabled = theme !== "hc" && status !== "failed";

  useEffect(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    const labels = labelsRef.current;
    if (!enabled || !host || !canvas || !labels) return;

    let disposed = false;
    let handle: GlobeHandle | null = null;

    const syncActive = () => {
      const box = host.getBoundingClientRect();
      handle?.setActive(
        document.visibilityState === "visible" && box.bottom > 0 && box.top < window.innerHeight,
      );
    };

    const start = () => {
      import("./hero3d/scene")
        .then(({ createGlobeScene }) =>
          createGlobeScene({
            canvas,
            host,
            labels,
            reducedMotion: prefersReducedMotion(),
            onContextLost: () => setStatus("failed"),
          }),
        )
        .then((created) => {
          if (disposed) {
            created.dispose();
            return;
          }
          handle = created;
          handleRef.current = created;
          created.setStats(statsRef.current);
          setStatus("ready");
          syncActive();
        })
        .catch((error: unknown) => {
          console.warn("Hero 3D sahnasi yuklanmadi, zaxira rasm ko'rsatiladi:", error);
          if (!disposed) setStatus("failed");
        });
    };

    // LCP va asosiy kontent bilan raqobatlashmasligi uchun — brauzer bo'shaganda
    const idle = window.requestIdleCallback
      ? window.requestIdleCallback(start, { timeout: 1200 })
      : window.setTimeout(start, 250);
    const unwatch = watchViewport(syncActive);
    document.addEventListener("visibilitychange", syncActive);

    return () => {
      disposed = true;
      if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
      else window.clearTimeout(idle);
      unwatch();
      document.removeEventListener("visibilitychange", syncActive);
      handle?.dispose();
      handle = null;
      handleRef.current = null;
    };
  }, [enabled]);

  /* Skan tugaganda chiqadigan ko'rsatkichlar (server javobi kelgach yangilanadi). */
  const statsKey = stats.map((item) => `${item.value}|${item.label}`).join(";");
  useEffect(() => {
    handleRef.current?.setStats(statsRef.current);
  }, [statsKey]);

  if (!enabled) return <>{fallback}</>;

  return (
    <div className={`hero-globe${status === "ready" ? " is-ready" : ""}`} ref={hostRef} aria-hidden="true">
      <canvas ref={canvasRef} />
      <div className="hero-globe-labels" ref={labelsRef} />
    </div>
  );
}
